/**
 * River rendering — v0M2.6g.
 *
 * v0M2.6g upgrade: rivers draw as smooth cubic-bezier curves through
 * edge-midpoint waypoints instead of straight-line segments. Combined
 * with v0M2.6g's downhill-flow river routing (steepest-descent from
 * high-elevation source to sea), the visual result is a naturalistic
 * winding stream instead of blocky orthogonal ticks.
 *
 * Rivers are stored in the painter JSON as cell-edge segments per
 * MAP.md §1.1. An edge [x, y, "E"] means the eastern edge of cell (x, y);
 * [x, y, "S"] the southern edge; ["W"] western; ["N"] northern. We
 * derive a waypoint (world-space (x, y) point) as the midpoint of each
 * edge, then feed the waypoint sequence to a cardinal-spline path.
 */

import type { Camera } from './camera';
import type { LoadedMap } from './map-loader';
import { pxPerCell, worldToScreen } from './camera';

const RIVER_STROKE = 'rgb(84, 122, 152)';
const RIVER_STROKE_UNDER = 'rgba(30, 60, 90, 0.75)';

/** Cardinal-spline tension. 0.5 gives Catmull-Rom-like curves. */
const SPLINE_TENSION = 0.5;

function edgeMidpoint(
  edge: readonly [number, number, 'E' | 'S' | 'W' | 'N'],
): { x: number; y: number } {
  const [x, y, dir] = edge;
  switch (dir) {
    case 'E':
      return { x: x + 1, y: y + 0.5 };
    case 'W':
      return { x, y: y + 0.5 };
    case 'S':
      return { x: x + 0.5, y: y + 1 };
    case 'N':
      return { x: x + 0.5, y };
  }
}

export function drawRivers(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const z = pxPerCell(camera);
  const strokeMain = Math.max(1.5, Math.min(4, z * 0.35));
  const strokeUnder = strokeMain + 1.4;

  for (const river of map.rivers) {
    if (river.edges.length === 0) continue;

    // Convert edge sequence to world-space waypoints. Deduplicate
    // consecutive identical points (happens when two edges share a
    // midpoint at a corner).
    const worldPoints: { x: number; y: number }[] = [];
    for (const e of river.edges) {
      const m = edgeMidpoint(e);
      const last = worldPoints[worldPoints.length - 1];
      if (!last || Math.abs(last.x - m.x) > 0.01 || Math.abs(last.y - m.y) > 0.01) {
        worldPoints.push(m);
      }
    }
    if (worldPoints.length < 2) continue;

    // Convert to screen space
    const screenPoints = worldPoints.map((p) =>
      worldToScreen(camera, canvasW, canvasH, p.x, p.y),
    );

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Under-stroke for depth
    ctx.strokeStyle = RIVER_STROKE_UNDER;
    ctx.lineWidth = strokeUnder;
    drawCardinalSpline(ctx, screenPoints);

    // Main stroke
    ctx.strokeStyle = RIVER_STROKE;
    ctx.lineWidth = strokeMain;
    drawCardinalSpline(ctx, screenPoints);

    ctx.restore();
  }
}

/**
 * Cardinal spline through the given points. Draws cubic bezier segments
 * between consecutive points using the neighbouring points to compute
 * tangents. Endpoints are handled by duplicating the terminal point.
 */
function drawCardinalSpline(
  ctx: CanvasRenderingContext2D,
  pts: ReadonlyArray<{ x: number; y: number }>,
): void {
  const n = pts.length;
  if (n < 2) return;

  ctx.beginPath();
  ctx.moveTo(pts[0]!.x, pts[0]!.y);

  const t = SPLINE_TENSION;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? pts[i + 1]!;

    // Cardinal-spline control points
    const cp1x = p1.x + ((p2.x - p0.x) * t) / 3;
    const cp1y = p1.y + ((p2.y - p0.y) * t) / 3;
    const cp2x = p2.x - ((p3.x - p1.x) * t) / 3;
    const cp2y = p2.y - ((p3.y - p1.y) * t) / 3;

    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
  }
  ctx.stroke();
}
